import { TextButton } from '@creditornot/cb-button';
import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Item as ItemT } from 'apis/restaurant-api/merchant-admin/menu2/types';
import BulkActionsToolbar from 'modules/menu/bulk-actions/components';
import useBulkSelection from 'modules/menu/bulk-actions/useBulkSelection';
import { useAllDisabledDurationViewTracking } from 'modules/menu/hooks/telemetryHooks';
import OptionModal from 'modules/menu/options/components/OptionModal';
import OptionSidePanel from 'modules/menu/options/components/OptionSidePanel';
import useMenuData from 'modules/menu/useMenuData';
import { isMenuEditorItem } from 'modules/menu/utils';

import { transformItemInventoryToQuantity } from './Item/utils';
import ItemSidePanel from './ItemSidePanel';
import { MenuItemsDnDMonitor } from './MenuItemsDnDMonitor';
import NewItemSidePanel from './NewItemSidePanel';
import MenuEditorCollectionList from '../../components/MenuEditorCollectionList';
import useMenuEditorLists from '../../hooks/useMenuEditorLists';

type Props = {
  menuEditorRootUrl: string;
  showWoltSpecials: boolean;
  onMoveItemsToCategory: (activeId: string, overId: string, selectedIds?: Set<string>) => Promise<void>;
  onMoveItemToNoCategory: (activeId: string) => Promise<void>;
  onAssignNoCategoryItemsToCategory: (activeId: string, overId: string, selectedIds?: Set<string>) => Promise<void>;
};

const ToastContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const ToastLink = styled(TextButton).attrs({ variant: 'blue', size: 'small' })`
  margin-block-start: 8px;
`;

const AllDisabled = ({
  menuEditorRootUrl,
  showWoltSpecials,
  onAssignNoCategoryItemsToCategory,
  onMoveItemToNoCategory,
  onMoveItemsToCategory,
}: Props) => {
  const { menu } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  const intl = useIntl();

  const {
    menuEditorList,
    activeItem,
    activeOption,
    searchFilter,
    itemInventory,
    newItemSidePanelProps,
    newOptionModalProps,
    setActiveItemId,
    setActiveOptionId,
    setSearchFilter,
    handleItemCopy,
    handleOptionCopy,
    refetchInventory,
    itemConfigs,
    updateItemConfig,
    itemCommissions,
    refetchItemCommissions,
  } = useMenuEditorLists('all-disabled', { showWoltSpecials });

  const { selectedIds, selectMultiple, deselectMultiple, clearSelection } = useBulkSelection(menuEditorList);

  const activeItemQuantity = useMemo(() => {
    const itemQuantity = itemInventory?.find(inventory => inventory.itemId === activeItem?.id);
    return itemQuantity ? transformItemInventoryToQuantity(itemQuantity) : undefined;
  }, [activeItem?.id, itemInventory]);

  const activeItemConfig = useMemo(() => {
    const activeItemId = activeItem?.id;
    return activeItemId ? itemConfigs[activeItemId] : undefined;
  }, [activeItem?.id, itemConfigs]);

  const handleItemEnableSuccess = (item: ItemT) => {
    setActiveItemId(undefined);

    const itemName = item.name.find(t => t.lang === menu.primary_language)?.value || item.name[0].value;
    toast.success(
      <ToastContent>
        {intl.formatMessage({ id: 'menu.itemEnableSuccess' }, { item: itemName })}
        <ToastLink>
          <Link to={menuEditorRootUrl}>{intl.formatMessage({ id: 'menu.goToAllItems' })}</Link>
        </ToastLink>
      </ToastContent>,
    );
  };

  const handleItemEnableFail = (item: ItemT) => {
    const itemName = item.name.find(t => t.lang === menu.primary_language)?.value || item.name[0].value;
    toast.error(<ToastContent>{intl.formatMessage({ id: 'menu.itemEnableFail' }, { item: itemName })}</ToastContent>);
  };

  const allFilteredItemIds = useMemo(() => {
    return menuEditorList.filter(isMenuEditorItem).map(item => item.itemState.id);
  }, [menuEditorList]);

  const handleSelectAllItems = useCallback(() => {
    selectMultiple(allFilteredItemIds);
  }, [allFilteredItemIds, selectMultiple]);

  const showBulkActionsToolbar = menuEditorList.some(el => el.type === 'item');

  const handleMoveItemsToCategory = useCallback(
    async (activeId: string, overId: string, selectedIds?: Set<string>) => {
      await onMoveItemsToCategory(activeId, overId, selectedIds);
      if (selectedIds && selectedIds.size > 1) {
        clearSelection();
      }
    },
    [clearSelection, onMoveItemsToCategory],
  );

  const handleAssignNoCategoryItemsToCategory = useCallback(
    async (activeId: string, overId: string, selectedIds?: Set<string>): Promise<void> => {
      await onAssignNoCategoryItemsToCategory(activeId, overId, selectedIds);
      if (selectedIds && selectedIds.size > 1) {
        clearSelection();
      }
    },
    [clearSelection, onAssignNoCategoryItemsToCategory],
  );
  useAllDisabledDurationViewTracking();

  const handleOptionClick = useCallback(
    (optionId: string) => {
      setActiveItemId(undefined);
      setActiveOptionId(optionId !== activeOption?.id ? optionId : undefined);
    },
    [setActiveItemId, setActiveOptionId, activeOption],
  );

  const handleItemClick = useCallback(
    (itemId: string) => {
      setActiveOptionId(undefined);
      setActiveItemId(itemId !== activeItem?.id ? itemId : undefined);
    },
    [setActiveItemId, setActiveOptionId, activeItem],
  );

  return (
    <MenuItemsDnDMonitor
      menuEditorList={menuEditorList}
      menu={menu}
      onAssignNoCategoryItemsToCategory={handleAssignNoCategoryItemsToCategory}
      onMoveItemsToCategory={handleMoveItemsToCategory}
      onMoveItemToNoCategory={onMoveItemToNoCategory}
    >
      {({ draggingId, displayMenuEditorList }) => (
        <>
          <MenuEditorCollectionList
            title={intl.formatMessage({ id: 'menu.allDisabled' })}
            description={intl.formatMessage({ id: 'menu.allDisabledDescription' })}
            search={searchFilter}
            onSearch={setSearchFilter}
            toolbar={
              showBulkActionsToolbar ? (
                <BulkActionsToolbar
                  commissions={itemCommissions}
                  onSelectAll={handleSelectAllItems}
                  onClearSelection={clearSelection}
                  disabled={!!activeItem || !!activeOption}
                  refetchCommissions={refetchItemCommissions}
                  selectAllItemsCount={allFilteredItemIds.length}
                  selectedIds={selectedIds}
                />
              ) : null
            }
            itemData={{
              menuId: menu.id,
              list: displayMenuEditorList,
              itemInventory,
              activeItemId: activeItem?.id,
              activeOptionId: activeOption?.id,
              selectedIds,
              canDrag: true,
              draggingId,
              disableDraggingSpecials: true,
              onEnableSuccess: handleItemEnableSuccess,
              onEnableFail: handleItemEnableFail,
              onItemClick: handleItemClick,
              onOptionClick: handleOptionClick,
              onSelectItems: selectMultiple,
              onDeselectItems: deselectMultiple,
              shouldRenderAllDisabledActions: true,
              onItemCopy: handleItemCopy,
              onOptionCopy: handleOptionCopy,
            }}
          >
            {!!activeItem && (
              <ItemSidePanel
                item={activeItem}
                menu={menu}
                itemQuantity={activeItemQuantity}
                onItemBalanceUpdated={refetchInventory}
                onClose={() => setActiveItemId(undefined)}
                itemConfig={activeItemConfig}
                onItemConfigUpdated={updateItemConfig}
                itemCommissions={itemCommissions}
                onItemCommissionsUpdated={refetchItemCommissions}
              />
            )}
            {!!activeOption && (
              <OptionSidePanel
                optionState={activeOption}
                menuId={menu.id}
                onClose={() => setActiveOptionId(undefined)}
              />
            )}
            {newItemSidePanelProps.isOpen && (
              <NewItemSidePanel {...newItemSidePanelProps} onItemConfigUpdated={updateItemConfig} />
            )}
          </MenuEditorCollectionList>
          <OptionModal {...newOptionModalProps} />
        </>
      )}
    </MenuItemsDnDMonitor>
  );
};

export default AllDisabled;
